// collector/network.js
//
// ONE network detector for the collector's screens (collector-app P1-C),
// shared by the day page, the shop page and the action layer. "Online" means
// OUR SERVER ANSWERS — not google.com, not navigator.onLine: a phone in a
// market can show bars and still not reach us.
//
// Two kinds of evidence feed it:
//   - a probe: `HEAD <api origin>/up` (Laravel's health route), `no-cors` so no
//     CORS preflight or credentials ride on it, `no-store` so no cache can
//     answer for the network, aborted after PROBE_TIMEOUT_MS (15 s). It runs on the
//     browser's online/offline events, every 30 s while online and every 15 s
//     while offline. (Not the ping.js favicon image: the service worker caches
//     same-origin images, so in the same-origin tunnel setup a cache-busted
//     favicon probe would churn that cache every poll.)
//   - the action layer's own requests: any HTTP answer → online; a network
//     error or a timeout → offline (`report()`).
//
// Phase 2 (offline) replays the queue when this flips back to online.
import { onBeforeUnmount, onMounted } from 'vue';
import { createNetworkDetector } from '@/composables/useNetworkDetector.js';

// As long as a tap's own request may take (ACTIONS_TIMEOUT_MS): on a slow link
// a shorter probe would read "offline" while a batch could still get through,
// and the queue would stall behind it.
const PROBE_TIMEOUT_MS = 15_000;
const ONLINE_POLL_MS = 30_000;
const OFFLINE_POLL_MS = 15_000;

/** The API's origin: VITE_API_BASE_URL, or this page's origin when it is empty (tunnel). */
export function apiOrigin() {
  const base = import.meta.env?.VITE_API_BASE_URL;
  if (typeof base === 'string' && base.trim() !== '') return base.replace(/\/+$/, '');
  return typeof window !== 'undefined' ? window.location.origin : '';
}

/** Resolve true when our server answers at all, false on a network error or timeout. */
export async function probeApi() {
  const controller = typeof AbortController === 'function' ? new AbortController() : null;
  const timer = controller ? setTimeout(() => controller.abort(), PROBE_TIMEOUT_MS) : null;
  try {
    await fetch(`${apiOrigin()}/up`, {
      method: 'HEAD',
      mode: 'no-cors',
      cache: 'no-store',
      credentials: 'omit',
      signal: controller?.signal,
    });
    return true;
  } catch {
    return false;
  } finally {
    if (timer) clearTimeout(timer);
  }
}

let detector = null;

/** The shared instance (created on first use). */
export function collectorNetwork() {
  if (!detector) {
    detector = createNetworkDetector(apiOrigin(), {
      probe: probeApi,
      pollIntervalMs: ONLINE_POLL_MS,
      offlinePollIntervalMs: OFFLINE_POLL_MS,
    });
  }
  return detector;
}

/** Hold the shared detector for a component's lifetime (refcounted). */
export function useCollectorNetwork() {
  const shared = collectorNetwork();
  onMounted(() => shared.start());
  onBeforeUnmount(() => shared.stop());
  return shared;
}

/** Tests only: forget the shared instance. */
export function resetCollectorNetworkForTests() {
  detector?.stop();
  detector = null;
}
